import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { VerifiedAuthenticationModal } from "@/features/Authentication/components/AuthenticationModal";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { StorePackageDetail } from "../../StorePackageModal/components/StorePackageDetail";
import { StorePackageDetailFooter } from "../../StorePackageModal/components/StorePackageDetailFooter";
import { StoreStackNavigatorParamList } from "../../StoreStackNavigator";
import { StoreProductDetailPageQuery } from "./__generated__/StoreProductDetailPageQuery.graphql";

export const StoreProductDetailPage = () => {
  const route =
    useRoute<RouteProp<StoreStackNavigatorParamList, "ProductDetail">>();
  const productId = route.params.productId;

  const [queryRef, loadQuery] = useQueryLoader<StoreProductDetailPageQuery>(
    StoreProductDetailPageQueryString
  );

  useEffect(() => {
    if (productId) {
      loadQuery({ productId }, { fetchPolicy: "store-and-network" });
    }
  }, [productId, loadQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? <StoreProductDetailPageInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<StoreProductDetailPageQuery>;
};
const StoreProductDetailPageInner = ({ queryRef }: InnerProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const navigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();
  const insets = useSafeAreaInsets();

  const data = usePreloadedQuery(StoreProductDetailPageQueryString, queryRef);

  const onBackPress = () => {
    navigation.goBack();
  };

  const productName = usePackageDisplayName(data.product);

  const onSuccessfulDraftAssignment = (
    memberPackageId: string,
    orderId: string
  ) => {
    navigation.navigate("ProductOrder", {
      orderId: orderId,
      productId: memberPackageId,
    });
  };

  const onSignUpPress = () => {
    setIsAuthModalOpen(true);
  };

  const club = data.session?.currentClientProfile?.member?.club;
  const clubSlug = club?.slug;
  const sessionClub = club
    ? {
        id: club.id,
        slug: club.slug,
        name: club.name,
        logoImage: club.logoImage?.publicId || "",
        supportsPublic: club.supportsPublic,
      }
    : null;

  return (
    <Box
      bg="surface-primary"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
    >
      {isAuthModalOpen ? (
        <Container flex={1} paddingHorizontal={0}>
          <VerifiedAuthenticationModal
            fallBack={<Loader />}
            onClose={() => {
              setIsAuthModalOpen(false);
            }}
            clubSlug={clubSlug}
            onComplete={() => {
              setIsAuthModalOpen(false);
            }}
            sessionClub={sessionClub}
          />
        </Container>
      ) : (
        <Container flex={1} paddingHorizontal={0}>
          <ScreenHeader
            title={productName}
            leftItem={
              <Button
                variant="secondary"
                accessibilityLabel={t`Back Button`}
                leftIcon={<ArrowLeftIcon />}
                onPress={onBackPress}
                testID="mobile-header-back-button"
              />
            }
            testID="membership-screen-header"
          />
          <ScrollView style={{ paddingTop: 16 }}>
            <StorePackageDetail packageRef={data.product} />
          </ScrollView>
          <StorePackageDetailFooter
            packageRef={data.product}
            sessionRef={data.session}
            onSignUpPress={onSignUpPress}
            onSuccessfulDraftAssignment={onSuccessfulDraftAssignment}
          />
        </Container>
      )}
    </Box>
  );
};

const StoreProductDetailPageQueryString = graphql`
  query StoreProductDetailPageQuery($productId: ID!) {
    session {
      ...StorePackageDetailFooter_session
      currentClientProfile {
        member {
          club {
            id
            slug
            name
            supportsPublic
            logoImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
    product: node(id: $productId) {
      ... on Package {
        ...usePackageDisplayName_package
        ...StorePackageDetail_package
        ...StorePackageDetailFooter_package
      }
    }
  }
`;
